"use client";

/**
 * Meta Pixel event helpers.
 *
 * Kept out of meta_pixel.tsx so that module exports only the component -
 * mixing components and plain helpers in one file breaks Fast Refresh.
 */

/**
 * Generate a unique event ID for deduplication between Pixel and Conversions API
 */
export function generateEventId(): string {
	return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
}

/**
 * Get Meta cookies for Conversions API matching
 */
export function getMetaCookies(): { fbc: string | null; fbp: string | null } {
	if (typeof document === "undefined") {
		return { fbc: null, fbp: null };
	}

	const cookies = document.cookie.split(";").reduce(
		(acc, cookie) => {
			const [key, value] = cookie.trim().split("=");
			acc[key] = value;
			return acc;
		},
		{} as Record<string, string>,
	);

	return {
		fbc: cookies._fbc || null,
		fbp: cookies._fbp || null,
	};
}

/**
 * Track standard Meta Pixel event with deduplication support
 * The eventId should be passed to server-side Conversions API for deduplication
 */
function trackMetaEvent(
	eventName: string,
	params?: Record<string, unknown>,
	eventId?: string,
): string {
	const id = eventId || generateEventId();

	if (typeof window !== "undefined" && window.fbq) {
		window.fbq("track", eventName, params, { eventID: id });
	}

	return id;
}

/**
 * Track Lead event (client-side)
 */
export function trackLeadPixel(params?: {
	contentName?: string;
	eventId?: string;
}): string {
	return trackMetaEvent(
		"Lead",
		{ content_name: params?.contentName || "Contact Form" },
		params?.eventId,
	);
}

/**
 * Track InitiateCheckout event (client-side)
 */
export function trackInitiateCheckoutPixel(params: {
	value: number;
	currency: string;
	contentName?: string;
	contentIds?: string[];
	numItems?: number;
	eventId?: string;
}): string {
	return trackMetaEvent(
		"InitiateCheckout",
		{
			value: params.value,
			currency: params.currency,
			content_name: params.contentName,
			content_ids: params.contentIds,
			num_items: params.numItems || 1,
		},
		params.eventId,
	);
}

// TypeScript declaration for fbq
declare global {
	interface Window {
		fbq: (
			action: "init" | "track" | "trackCustom",
			eventNameOrPixelId: string,
			params?: Record<string, unknown>,
			options?: { eventID?: string },
		) => void;
	}
}
